import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/assets';
import { getGongdaoHistory, GongdaoRecord } from '../../api/history';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { selectBalance, setUserProfile } from '../../data/accountSlice';
import { getUserProfile } from '../../api/user';
import Loading from './Loading';
import './GongdaozhiModal.css';

interface GongdaozhiModalProps {
  visible: boolean;
  onClose: () => void;
}

const GongdaozhiModal: React.FC<GongdaozhiModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation(['game']);
  const dispatch = useAppDispatch();
  const [records, setRecords] = useState<GongdaoRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const balance = useAppSelector(selectBalance);
  
  // Parse balance string to number for display, remove commas if any
  const totalValue = parseInt((balance || '0').replace(/,/g, ''), 10);

  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;

  useEffect(() => {
    if (visible) {
      fetchRecords();
    }
  }, [visible]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      // Fetch records
      const data = await getGongdaoHistory(1, 20);
      setRecords(data.list);
      
      // Also fetch latest user profile to update balance in store
      // This ensures the header value is synced
      const profile = await getUserProfile();
      dispatch(setUserProfile(profile));
    } catch (error) {
      console.error('Failed to fetch history or profile', error);
      toast.error(t('common.loadingError', 'Failed to load records'));
    } finally {
      setLoading(false);
    }
  };

  const formatShortDate = (timestamp: number) => {
    // Convert timestamp (seconds or ms) to Date
    // API docs say "Unix Timestamp (seconds or ms, unified as Long)"
    // Usually if it's recent it's seconds, but let's check length or assume seconds if small
    // Standard is usually seconds for blockchain/backend. 
    // Let's assume seconds if < 10000000000, else ms
    const date = new Date(timestamp > 10000000000 ? timestamp : timestamp * 1000);
    
    // Format: YY.MM.DD HH:mm:ss
    const year = date.getFullYear().toString().slice(2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    
    return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
  };

  // If invisible and no records loaded yet, render nothing to save resources
  if (!visible && records.length === 0 && !loading) return null;

  return (
    <div className={`gongdaozhi-modal-container ${visible ? 'visible' : ''}`}>
      <div className="gongdaozhi-backdrop" style={{ backgroundImage: getImg('popup-backdrop.png') }} onClick={onClose} />

      <div className="gongdaozhi-content-wrapper">
        {/* Background Overlay */}
        <div className="gdz-background-overlay"/>
        
        {/* Content Panel */}
        <div className="gdz-content-panel" style={{ backgroundImage: getImg('gdz_content_panel.png') }}>
          
          {/* Close Button */}
          <div className="gdz-close-button" style={{ backgroundImage: getImg('gdz_close_button.png') }} onClick={onClose} />
          
          {/* Title */}
          <div className="gdz-title-text">{t('gongdaozhi.title')}</div>
          
          {/* Details Title */}
          <div className="gdz-details-title">{t('gongdaozhi.detailsTitle')}</div>
          
          {/* Header Bar Area */}
          <div className="gdz-header-bar" style={{ backgroundImage: getImg('gdz_header_bar.png') }} />
          <div className="gdz-header-label">{t('gongdaozhi.currentLabel')}</div>
          <div className="gdz-total-value">{totalValue}</div>
          <div className="gdz-header-badge" style={{ backgroundImage: getImg('gdz_value_badge.png') }} />
          
          {/* Scrollbar Visuals */}
          <div className="gdz-scrollbar-track" style={{ backgroundImage: getImg('gdz_scrollbar_track.png') }} />
          <div className="gdz-scrollbar-thumb" style={{ backgroundImage: getImg('gdz_scrollbar_thumb.png') }} />
          
          {/* List Container */}
          <div className="gdz-list-container">
            {records.map((record) => (
              <div key={record.id} className="gdz-list-item">
                <div className="gdz-record-icon" style={{ backgroundImage: getImg('gdz_record_icon.png') }} />
                {/* API returns localized type string, so display directly */}
                <div className="gdz-record-type-text">{record.type}</div>
                <div className="gdz-date-text">{formatShortDate(record.timestamp)}</div>
                <div className="gdz-value-change-text">
                  {record.direction === 'income' ? '+' : '-'}{record.amount}
                </div>
                <div className="gdz-divider-line" style={{ backgroundImage: getImg('gdz_divider_line.png') }} />
              </div>
            ))}
          </div>

          {/* Refresh/Action Button */}
          <div className="gdz-refresh-button" style={{ backgroundImage: getImg('gdz_button_primary.png') }} onClick={fetchRecords}>
             <div className="gdz-refresh-text">{t('gongdaozhi.getRecords')}</div>
          </div>

          {loading && <Loading />}

        </div>
      </div>
    </div>
  );
};

export default GongdaozhiModal;
